<template>
    <ConfigureUserModal
        v-model="input"
        :rp-name="rpName"
        :submitting="submitting"
        :disable-name="!isAuth0User"
        :name-warning="isAuth0User ? undefined : 'Name cannot be edited for users with external provider (i.e. Google)'"
        :title="`Edit ${user.email} user`"
        disable-email-address
        open
        is-edit
        submit-label="Save"
        @submit="onEditRequest"
        @close="$emit('abort')"
    />
</template>

<script lang="ts">
    import { Component, Prop, Watch, Vue } from '@evidentid/vue-property-decorator';

    import { toRbacRole } from '@evidentid/tprm-portal-lib/utils/common/toRbacRole';
    import delay from '@evidentid/universal-framework/delay';
    import { User } from '@evidentid/user-management-api-client/types';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfigureUserModal from '../components/ConfigureUserModal.vue';
    import { UserInput } from '../types';
    import { useUserManagementStore } from '@/modules/user-management/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: {
            ConfigureUserModal,
        },
        emits: [ 'abort', 'finish' ],
    })
    export default class EditUserProcedure extends Vue {
        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String })
        private rpName!: string;

        @Prop({ type: Object })
        private user!: User;
        // TODO[PRODUCT-24516]: use a proper fix instead of delay
        private delayLoading = false;

        private input: UserInput = {
            name: '',
            email: '',
            role: toRbacRole('viewer'),
        };

        private get status(): { status: OperationStatus, error: any } {
            return useUserManagementStore().updateUserStatus[this.rpName]?.[this.user.id] || {
                status: OperationStatus.uninitialized,
                error: null,
            };
        }

        private get isAuth0User(): boolean {
            return /^auth0\|/.test(this.user.id);
        }

        private get submitting() {
            return this.status.status === OperationStatus.loading || this.delayLoading;
        }

        @Watch('user', { immediate: true })
        private updateValueExternally() {
            this.input = {
                name: this.user.name || '',
                email: this.user.email || '',
                role: this.user.role,
            };
        }

        private success(message: string): void {
            useSnackbarStore().displaySnackbar({ message, success: true });
        }

        private error(message: string): void {
            useSnackbarStore().displaySnackbar({ message, success: false });
        }

        private async onEditRequest(input: UserInput): Promise<void> {
            if (this.status.status === OperationStatus.loading) {
                return;
            }

            await useUserManagementStore().updateUser({
                rpName: this.rpName,
                id: this.user.id,
                input,
            });
            // TODO[PRODUCT-24516]: use a proper fix instead of delay
            this.delayLoading = true;
            await delay(1000);
            this.delayLoading = false;
            const { status, error } = this.status;
            const response = error?.response;
            if (status === OperationStatus.success) {
                this.success(`User information for ${input.email} was updated`);
                this.$emit('finish');
            } else if (error?.reason === 'forbidden' && response?.message?.includes?.('Not permitted to modify')) {
                this.error(`
                    This action is not allowed since the user has multiple accounts with Evident.
                    Please reach out to Evident support to invite this user.
                `);
            } else if (error?.reason === 'forbidden') {
                this.error('You are not allowed to change selected user.');
            } else {
                this.error('Oops, something went wrong. Please try again!');
            }
        }
    }
</script>
